import { publish } from '~/event-bus';
import { classes, debounce, getCartSectionNames, throttle, updateQueryParam } from '~/helpers';
import { HTMLCustomElement } from '~/html-custom-element';

class ProductForm extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on('submit', this.targets.form, this.handleFormSubmit.bind(this));
    this.on('change', this.targets.option && this.targets.option.all, () => this.handleOptionChange());
    this.on('change', this.targets['gift-card-form-checkbox'], this.handleGiftCardFormCheckbox.bind(this));
    this.on('scroll', window, throttle(this.handleWindowScroll.bind(this)));
    this.on('resize', window, debounce(this.handleWindowResize.bind(this)));

    if (this.targets.quantity) {
      this.on('change', this.targets.quantity.all, (event) => {
        for (const element of this.targets.quantity.all) {
          if (!(element === event.currentTarget) && !(element.value === event.currentTarget.value)) {
            element.value = event.currentTarget.value;

            /**
             * Emit native event.
             */
            element.dispatchEvent(new Event('change'));
          }
        }
      });
    }

    this.on('selling-plan-group-select', this, (event) => {
      if (event.detail === 'one-time-group') {
        updateQueryParam('selling_plan', null);
      }
    });

    setTimeout(() => {
      if (this.targets['selling-plan-group']) {
        this.targets['selling-plan-group'].all.forEach((element) => {
          if (element.targets.radio.checked && element.id !== 'one-time-group') {
            this.handleOptionChange(element.targets['selling-plan'].value);
          }
        });
      }
    });

    this.handleWindowResize();
  }

  /**
   * Handles the resize event.
   */
  handleWindowResize() {
    this.handleWindowScroll();

    if (this.targets.sticky) {
      this.targets.sticky.classList.toggle(classes.disabled, this.offsetHeight < window.innerHeight);
    }
  }

  /**
   * Handles the window scroll event.
   * @param {Event} _ - The scroll event.
   */
  handleWindowScroll(_) {
    const { bottom: submitBottom } = this.targets.submit.getBoundingClientRect();
    const submitIsAboveViewport = submitBottom < 0;

    this.targets.sticky.classList.toggle(
      classes.active,
      submitIsAboveViewport || this.getBoundingClientRect().bottom <= window.innerHeight,
    );

    if (!this.targets.sticky.classList.contains(classes.ready)) {
      requestAnimationFrame(() => {
        this.targets.sticky.classList.add(classes.ready);
      });
    }
  }

  /**
   * Handles the option change event.
   * @param {String} [sellingPlan] - The selling plan to handle.
   */
  handleOptionChange(sellingPlan) {
    const values = this.targets.option.all.filter(({ checked }) => checked).map(({ dataset }) => dataset.optionValueId);

    this.dispatchEvent(
      new CustomEvent('option-change', {
        detail: {
          values,
          sellingPlan,
        },
      }),
    );
  }

  /**
   * Handles the form submit event.
   */
  handleFormSubmit(event) {
    const formData = new FormData(event.target);

    event.preventDefault();

    this.setAddingState();
    this.setFormMessage();

    /**
     * Include the cart sections in the response.
     */
    formData.append('sections', getCartSectionNames());

    try {
      fetch(theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((response) => {
          this.handleSubmitResponse(response);
          this.setAddingState(true);
        });
    } catch {
      event.target.submit();
    }
  }

  /**
   * Handles the form submit response.
   * @param {Object} response - The form response.
   */
  handleSubmitResponse(response) {
    if (response.message) {
      this.setFormMessage(response.message);
      return;
    }

    publish('cart:update', { sections: response.sections });
    publish('cart:added');

    if (this.targets.quantity) {
      this.targets.quantity.all.forEach((element) => {
        element.value = 1;
        element.dispatchEvent(new Event('change'));
      });
    }
  }

  /**
   * Handles the gift card recipient form checkbox change.
   * @param {InputEvent} event - The checkbox event.
   */
  handleGiftCardFormCheckbox(event) {
    if (this.targets['gift-card-input']) {
      this.targets['gift-card-input'].all.forEach((element) => {
        if (event.target.checked) {
          element.classList.remove('display-none');
          return;
        }

        element.classList.add('display-none');
      });
    }
  }

  /**
   * Sets the form message state.
   * @param {String} text - The message to set.
   */
  setFormMessage(text = '') {
    if (this.targets.message) {
      this.targets.message.innerHTML = text;
    }
  }

  /**
   * Sets the form's "adding" state, when adding to cart.
   * @param {Boolean} remove - Removes the adding state.
   */
  setAddingState(remove = false) {
    if (!this.targets.button) return;

    this.targets.button.all.forEach((element) => {
      element.disabled = !remove;

      if (remove) {
        element.innerHTML = this.defaultSubmitState ?? theme.strings.sections.main_product.add_to_cart;
        return;
      }

      this.defaultSubmitState = element.innerHTML;
      element.innerHTML = theme.strings.sections.main_product.adding_to_cart;
    });
  }
}

class SellingPlanGroup extends HTMLCustomElement {
  /**
   * @returns {ProductForm}
   */
  get productForm() {
    return this.closest('product-form');
  }

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    if (!this.productForm) {
      return;
    }

    this.on('change', this.targets.radio, () => {
      this.productForm.dispatchEvent(
        new CustomEvent('selling-plan-group-select', {
          detail: this.id,
        }),
      );
    });

    this.on('selling-plan-group-select', this.productForm, (event) => {
      const isActive = this.id === event.detail;
      const select = this.querySelector('[name=selling_plan]');

      if (select) {
        select.disabled = !isActive;

        if (isActive) {
          select.dispatchEvent(new Event('change'));
        }
      }

      this.classList.toggle(classes.active, isActive);
    });

    this.on('change', this.targets['selling-plan'], (event) => {
      this.productForm.handleOptionChange(event.currentTarget.value);
    });

    this.on('click', this, () => {
      this.targets.radio.checked = true;

      this.targets.radio.dispatchEvent(new Event('change'));
    });
  }
}

customElements.define('product-form', ProductForm);
customElements.define('selling-plan-group', SellingPlanGroup);
